import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Tag, Button, Space, Card, Select, DatePicker, Row, Col, theme, Pagination } from 'antd'
import { BookOutlined, FilterOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { apiService, User } from '@/lib/api'
import DashboardLayout from '@/components/DashboardLayout'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

interface JournalLine {
  id: number
  account_code: string
  account_name: string
  account_type: string
  description: string
  debit: number
  credit: number
}

interface JournalEntry {
  id: number
  date: string
  reference: string
  type: string
  description: string
  status: string
  created_by: string | null
  related_document?: { type: string; id: number; number: string }
  lines: JournalLine[]
  totals: { debits: number; credits: number; is_balanced: boolean }
  running_totals: { debits: number; credits: number }
}

interface JournalResponse {
  success: boolean
  message?: string
  data?: JournalEntry[]
  pagination?: { page: number; per_page: number; total: number; total_pages: number; total_count?: number }
  summary?: { is_balanced: boolean; difference?: number; total_debits: number; total_credits: number }
}

export default function GeneralJournalPage() {
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const [user, setUser] = useState<User | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [statusFilter, setStatusFilter] = useState<string>('posted')
  const [typeFilter, setTypeFilter] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<[string, string] | undefined>()

  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) setUser(JSON.parse(storedUser))
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    navigate('/')
  }

  const { data: journalData, isLoading } = useQuery<JournalResponse>({
    queryKey: ['journal-view', page, perPage, statusFilter, typeFilter, dateRange],
    queryFn: async () => {
      const params: any = { page: page.toString(), per_page: perPage.toString() }
      if (statusFilter) params.status = statusFilter
      if (typeFilter) params.transaction_type = typeFilter
      if (dateRange) { params.start_date = dateRange[0]; params.end_date = dateRange[1] }
      const response = await apiService.getJournalView(params)
      if (response.success) return response as JournalResponse
      throw new Error(response.message || 'Failed to load journal')
    }
  })

  const handleClearFilters = () => { setStatusFilter('posted'); setTypeFilter(undefined); setDateRange(undefined); setPage(1) }
  const handlePrint = () => window.print()

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Invoice': return 'blue'; case 'Bill': return 'purple'; case 'Payment': return 'green'
      case 'Bill Payment': return 'cyan'; case 'Credit Note': return 'orange'; default: return 'default'
    }
  }

  return (
    <DashboardLayout user={user} onLogout={handleLogout} title="General Journal" subtitle="View all journal entries in traditional format" titleIcon={<BookOutlined />}
      headerActions={<Button icon={<PrinterOutlined />} onClick={handlePrint} className="no-print">Print</Button>}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card title={<><FilterOutlined /> Filters</>} className="no-print">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}><Select placeholder="Status" style={{ width: '100%' }} allowClear value={statusFilter} onChange={setStatusFilter} options={[{ value: 'draft', label: 'Draft' }, { value: 'posted', label: 'Posted' }, { value: 'void', label: 'Void' }]} /></Col>
            <Col xs={24} sm={12} md={6}><Select placeholder="Transaction Type" style={{ width: '100%' }} allowClear value={typeFilter} onChange={setTypeFilter} options={[{ value: 'Invoice', label: 'Invoice' }, { value: 'Bill', label: 'Bill' }, { value: 'Payment', label: 'Payment' }, { value: 'Bill Payment', label: 'Bill Payment' }, { value: 'Credit Note', label: 'Credit Note' }, { value: 'Journal Entry', label: 'Journal Entry' }]} /></Col>
            <Col xs={24} sm={12} md={8}><RangePicker style={{ width: '100%' }} value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null} onChange={(dates) => { if (dates && dates[0] && dates[1]) { setDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]) } else { setDateRange(undefined) } }} /></Col>
            <Col xs={24} sm={12} md={4}><Button icon={<ClearOutlined />} onClick={handleClearFilters} block>Clear</Button></Col>
          </Row>
        </Card>

        <Card loading={isLoading}>
          <div style={{ fontFamily: 'monospace', fontSize: 13 }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={3} style={{ margin: 0 }}>GENERAL JOURNAL</Title>
              {dateRange && <Text type="secondary">Period: {dayjs(dateRange[0]).format('DD MMM YYYY')} to {dayjs(dateRange[1]).format('DD MMM YYYY')}</Text>}
            </div>

            <Row style={{ borderBottom: `2px solid ${token.colorBorder}`, padding: '12px 8px', fontWeight: 'bold', backgroundColor: token.colorBgContainer }}>
              <Col span={3}>Date</Col><Col span={3}>Reference</Col><Col span={2}>Code</Col><Col span={5}>Account Name</Col><Col span={5}>Description</Col><Col span={3} style={{ textAlign: 'right' }}>Debit</Col><Col span={3} style={{ textAlign: 'right' }}>Credit</Col>
            </Row>

            {journalData?.data?.map((entry: JournalEntry) => (
              <div key={entry.id}>
                {entry.lines.map((line, lineIndex) => (
                  <Row key={line.id} style={{ padding: '10px 8px', borderBottom: `1px solid ${token.colorBorderSecondary}`, backgroundColor: lineIndex % 2 === 0 ? 'transparent' : token.colorBgLayout }}>
                    <Col span={3}>{lineIndex === 0 ? <Text>{dayjs(entry.date).format('DD MMM YY')}</Text> : <Text type="secondary">″</Text>}</Col>
                    <Col span={3}>{lineIndex === 0 ? <div><Text strong style={{ fontSize: 12 }}>{entry.reference}</Text><br /><Tag color={getTypeColor(entry.type)} style={{ marginTop: 2, fontSize: 10 }}>{entry.type}</Tag></div> : <Text type="secondary">″</Text>}</Col>
                    <Col span={2}><div style={{ paddingLeft: Number(line.credit) > 0 ? 12 : 0 }}><Text strong style={{ fontSize: 12 }}>{line.account_code}</Text></div></Col>
                    <Col span={5}><div style={{ paddingLeft: Number(line.credit) > 0 ? 12 : 0 }}><Text style={{ fontSize: 12 }}>{line.account_name}</Text><br /><Tag style={{ fontSize: 10, marginTop: 2 }}>{line.account_type}</Tag></div></Col>
                    <Col span={5}><Text type="secondary" style={{ fontSize: 11 }}>{line.description}</Text></Col>
                    <Col span={3} style={{ textAlign: 'right' }}>{Number(line.debit) > 0 && <Text strong style={{ color: token.colorError, fontSize: 13 }}>{Number(line.debit).toFixed(2)}</Text>}</Col>
                    <Col span={3} style={{ textAlign: 'right' }}>{Number(line.credit) > 0 && <Text strong style={{ color: token.colorSuccess, fontSize: 13 }}>{Number(line.credit).toFixed(2)}</Text>}</Col>
                  </Row>
                ))}
                <Row style={{ padding: '8px 8px', backgroundColor: token.colorBgContainer, borderBottom: `2px solid ${token.colorBorder}`, fontWeight: 'bold' }}>
                  <Col span={18} style={{ textAlign: 'right', paddingRight: 16 }}><Space size="small"><Text strong style={{ fontSize: 12 }}>Total for {entry.reference}</Text>{entry.totals.is_balanced && <Tag color="green">✓</Tag>}</Space></Col>
                  <Col span={3} style={{ textAlign: 'right' }}><Text strong style={{ color: token.colorError, fontSize: 13 }}>{Number(entry.totals.debits).toFixed(2)}</Text></Col>
                  <Col span={3} style={{ textAlign: 'right' }}><Text strong style={{ color: token.colorSuccess, fontSize: 13 }}>{Number(entry.totals.credits).toFixed(2)}</Text></Col>
                </Row>
              </div>
            ))}

            {(!journalData?.data || journalData.data.length === 0) && !isLoading && <div style={{ textAlign: 'center', padding: 48 }}><Text type="secondary">No journal entries found for the selected criteria.</Text></div>}

            {journalData?.summary && (
              <Row style={{ padding: '16px 8px', backgroundColor: token.colorPrimaryBg, borderTop: `3px double ${token.colorBorder}`, fontWeight: 'bold', fontSize: 14 }}>
                <Col span={18} style={{ textAlign: 'right', paddingRight: 16 }}>
                  <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                    <Text strong>PAGE TOTAL ({journalData.pagination?.total_count || 0} entries)</Text>
                    {journalData.summary.is_balanced ? <Tag color="green">✓ Balanced</Tag> : <Tag color="red">⚠ Out of Balance (Diff: {journalData.summary.difference?.toFixed(2)})</Tag>}
                  </Space>
                </Col>
                <Col span={3} style={{ textAlign: 'right' }}><Text strong style={{ color: token.colorError, fontSize: 15 }}>{Number(journalData.summary.total_debits).toFixed(2)}</Text></Col>
                <Col span={3} style={{ textAlign: 'right' }}><Text strong style={{ color: token.colorSuccess, fontSize: 15 }}>{Number(journalData.summary.total_credits).toFixed(2)}</Text></Col>
              </Row>
            )}
          </div>
        </Card>

        {journalData?.pagination && (
          <div style={{ textAlign: 'center' }} className="no-print">
            <Pagination current={page} pageSize={perPage} total={journalData.pagination.total_count} showSizeChanger showTotal={(total) => `Total ${total} entries`} onChange={(newPage, newPageSize) => { setPage(newPage); setPerPage(newPageSize) }} pageSizeOptions={['5', '10', '20', '50']} />
          </div>
        )}
      </Space>
    </DashboardLayout>
  )
}
